import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { map, switchMap, Observable, forkJoin } from 'rxjs';
import {
  GET_COLLECTOR,
  SEARCH_CAMPAIGNS_COLLECTORS_LIST,
  GET_WORKFLOW,
  GET_WORKFLOWS,
  GET_WORKFLOW_PRIORITY_BY_TENANT,
  GET_CAMPAIGNS_LIST,
} from '../graphql';
import { GET_SLIM_WORKFLOWS } from '@modules/campaigns/graphql/workflows.graphql';

export const CampaignListResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot) => {
  const apollo = inject(Apollo);

  return apollo
    .query({
      query: GET_WORKFLOWS,
      variables: {
        tenantId: route.queryParams['tenant'],
      },
    })
    .pipe(map((res: any) => res.data.workflows));
};

export const CampaignSlimListResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot) => {
  const apollo = inject(Apollo);

  return apollo
    .query({
      query: GET_SLIM_WORKFLOWS,
      variables: {
        tenantId: route.queryParams['tenant'],
      },
    })
    .pipe(map((res: any) => res.data.workflows));
};

export const CampaignSublistListResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot) => {
  const apollo = inject(Apollo);

  return apollo
    .query({
      query: GET_CAMPAIGNS_LIST,
      variables: {
        tenantId: route.queryParams['tenant'],
      },
    })
    .pipe(map((res: any) => res.data.campaigns));
};

export const CampaignsCollectorsResolver: ResolveFn<any> = (
  route: ActivatedRouteSnapshot,
): Observable<any> => {
  const apollo = inject(Apollo);
  const tenantId = route.queryParams['tenant'];

  return apollo
    .query({
      query: SEARCH_CAMPAIGNS_COLLECTORS_LIST,
      variables: {
        criteria: {
          tenantId: tenantId,
        },
      },
    })
    .pipe(
      switchMap((res: any) => {
        const campaignCollectors: any = res.data.campaignCollectors;
        const requests: Observable<any>[] = campaignCollectors.map((collector: any) =>
          apollo
            .query({
              query: GET_COLLECTOR,
              variables: {
                id: collector.collectorId,
              },
            })
            .pipe(map((response: any) => response.data.collector)),
        );

        return forkJoin(requests).pipe(
          map((responses: any) => {
            const updatedCampaignCollectors: any = campaignCollectors.map(
              (collector: any, index: number) => {
                const collectorDetail: any = responses[index];
                return {
                  ...collector,
                  detail: collectorDetail,
                };
              },
            );

            return updatedCampaignCollectors;
          }),
        );
      }),
    );
};

export const CampaignProrityResolver: ResolveFn<any> = (
  route: ActivatedRouteSnapshot,
): Observable<any> => {
  const apollo = inject(Apollo);
  const tenantId = route.queryParams['tenant'];

  return apollo
    .query({
      query: GET_WORKFLOW_PRIORITY_BY_TENANT,
      variables: {
        tenantId,
      },
    })
    .pipe(
      map((res: any) => {
        if (res.errors?.length) {
          throw new Error(res.errors[0].message);
        } else {
          return res.data.workflowPriorityByTenant.workflowIds;
        }
      }),
    );
};

export const getCampaign: ResolveFn<any> = (route: ActivatedRouteSnapshot): Observable<any> => {
  const apollo = inject(Apollo);

  return apollo
    .query({
      query: GET_WORKFLOW,
      variables: {
        id: route.params['id'],
      },
    })
    .pipe(
      map((res: any) => {
        if (res.errors?.length) {
          throw new Error(res.errors[0].message);
        } else {
          return res.data.workflow;
        }
      }),
    );
};
